import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import {
    VehicleProtectionCompactActivity,
    VehicleProtectionFlyOutCardFooter,
    useVehicleProtectionContext
} from '@makemydeal/dr-activities-vehicle-protection';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { UnsavedChangesConfirmationModal, useUnsavedChangesModal } from '@makemydeal/dr-dash-components';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { Badge } from '@interstate/components/Badge';
import { Alert } from '@interstate/components/Alert';
import { Action } from '@interstate/components/Action';
import { SlideOutWrapper } from './SlideOutWrapper';

import type { SlideOutComponentProps } from '../../types';
import useOpenInDarwin from '@makemydeal/dr-dash-store/hooks/useOpenInDarwin';

export const ProtectionProductSlideOutContent = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const dispatch = useDispatch();
    const selectedManualVppProducts = useSelector(vehicleProtectionSelectors.getSelectedManualVppProducts, isEqual);
    const handleOpenInDarwin = useOpenInDarwin();
    const { isDarwinVppEditGuardEnabled, isDarwinVPPEditDisabled } = useVehicleProtectionContext();

    const handleOnClose = useCallback(
        (options?: { cancelled?: boolean }) => {
            dispatch(interactionAnalyticsActionCreators.applyVehicleProtectionButton());
            onClose(options);
        },
        [dispatch, onClose]
    );

    const { showConfirmationModal, handleClose, handleCancelChanges, handleContinueEditing, onDirtyCheck } = useUnsavedChangesModal(
        {
            savedData: selectedManualVppProducts,
            onClose: handleOnClose
        }
    );

    const handleBackClick = () => {
        handleClose();
    };

    const title = (
        <>
            Vehicle Protection Products
            {isDarwinVppEditGuardEnabled && (
                <Badge data-testid="darwin-managed-badge" variant="light" sx={{ marginLeft: 1 }}>
                    Darwin Managed
                </Badge>
            )}
        </>
    );

    const darwinInfoBanner = isDarwinVppEditGuardEnabled ? (
        <Alert data-testid="darwin-info-banner" role="banner" type="info" sx={{ marginBottom: 1 }}>
            Vehicle Protection Products fields are managed by Darwin. Make changes in Darwin and save them back to Deal Central to
            keep everything in sync.{' '}
            <Action data-testid="darwin-info-banner-link" onClick={handleOpenInDarwin}>
                Open in Darwin
            </Action>
        </Alert>
    ) : null;

    const darwinCautionBanner = isDarwinVppEditGuardEnabled ? (
        <Alert data-testid="darwin-caution-banner" role="banner" type="caution" sx={{ marginBottom: 1 }}>
            Darwin is the system of record for vehicle protection products on this deal. Edits made here may conflict with Darwin
            and will not sync back. Proceed with caution because changes could cause discrepancies with contracts generated in
            Darwin.{' '}
            <Action data-testid="darwin-info-banner-link" onClick={handleOpenInDarwin}>
                Open in Darwin
            </Action>
        </Alert>
    ) : null;

    return (
        <>
            <SlideOutWrapper
                title={title}
                isOpen={isOpen}
                onClose={handleBackClick}
                panelWidth="95vw"
                testId="tab-slideout-protection"
                footer={{
                    footerComponent: <VehicleProtectionFlyOutCardFooter onClose={handleBackClick} />
                }}
            >
                {isDarwinVPPEditDisabled ? darwinInfoBanner : darwinCautionBanner}
                <VehicleProtectionCompactActivity onDirtyCheck={onDirtyCheck} shouldTriggerValidation={showConfirmationModal} />
            </SlideOutWrapper>
            <UnsavedChangesConfirmationModal
                show={showConfirmationModal}
                onCancelChanges={handleCancelChanges}
                onContinueEditing={handleContinueEditing}
            />
        </>
    );
};
